import { useState, type DragEvent } from 'react';
import { Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { Button, Field } from '@mailflow/ui';
import {
  boardQuery,
  formatMoney,
  useCreateDeal,
  useMoveDeal,
  type BoardStage,
} from '../lib/crm.js';
import { contactsQuery } from '../lib/contacts.js';
import { useDebounced } from '../lib/useDebounced.js';

/**
 * Verkaufstafel.
 *
 * Ziehen und Ablegen ueber die Bordmittel des Browsers statt ueber eine
 * Bibliothek: eine Kanban-Tafel braucht genau ein Ereignis (welche Karte in
 * welche Spalte), und dafuer 40 kB Abhaengigkeit mitzuschleppen waere
 * unverhaeltnismaessig. Wichtiger: es bleibt ohne Maus bedienbar — jede Karte
 * hat zusaetzlich eine Auswahl fuer die Stufe.
 */
export function CrmBoardPage() {
  const [viewMode, setViewMode] = useState<'board' | 'split'>('split');
  const [selectedDealId, setSelectedDealId] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [searchFilter, setSearchFilter] = useState<string>('');

  const board = useQuery(boardQuery);
  const move = useMoveDeal();
  const [dragging, setDragging] = useState<string | null>(null);
  const [over, setOver] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  function handleDrop(event: DragEvent<HTMLElement>, stageId: string) {
    event.preventDefault();
    setOver(null);
    const dealId = event.dataTransfer.getData('text/plain') || dragging;
    setDragging(null);
    if (!dealId) return;

    const from = board.data?.stages.find((s) => s.deals.some((d) => d.id === dealId));
    if (from?.id === stageId) return;
    move.mutate({ id: dealId, stageId });
  }

  if (board.isLoading) return <p className="muted">Wird geladen …</p>;

  if (board.isError) {
    return (
      <div className="alert" data-tone="danger" role="alert">
        {board.error instanceof Error ? board.error.message : 'Tafel konnte nicht geladen werden.'}
      </div>
    );
  }

  const data = board.data;
  if (!data) return null;

  const openDeals = data.stages
    .filter((stage) => stage.type === 'open')
    .reduce((total, stage) => total + stage.deals.length, 0);

  const allDeals = data.stages.flatMap((s) =>
    s.deals.map((d) => ({ ...d, stageName: s.name, stageId: s.id })),
  );
  const filteredDeals = allDeals.filter((d) => {
    if (statusFilter && d.stageId !== statusFilter) return false;
    if (searchFilter && !d.title.toLowerCase().includes(searchFilter.toLowerCase())) return false;
    return true;
  });

  return (
    <div className="stack" style={{ gap: 'var(--space-6)' }}>
      <header className="page-header">
        <div>
          <h1 className="text-2xl">{data.pipeline.name}</h1>
          <p className="muted text-sm">
            {openDeals} offene Rezensionsfälle · gewichteter Forecast{' '}
            <strong>{formatMoney(data.forecast, data.pipeline.currency)}</strong> — Summe aus Wert
            mal Wahrscheinlichkeit der Stufe.
          </p>
        </div>
        <div className="cluster">
          <div className="button-group">
            <Button
              variant={viewMode === 'split' ? 'primary' : 'ghost'}
              onClick={() => setViewMode('split')}
            >
              Split-View
            </Button>
            <Button
              variant={viewMode === 'board' ? 'primary' : 'ghost'}
              onClick={() => setViewMode('board')}
            >
              Kanban-Board
            </Button>
          </div>
          <Button onClick={() => setCreating(true)}>💼 Neuer Rezensionsfall</Button>
        </div>
      </header>

      {move.isError && (
        <div className="alert" data-tone="danger" role="alert">
          {move.error instanceof Error ? move.error.message : 'Verschieben fehlgeschlagen.'}
        </div>
      )}

      {viewMode === 'split' ? (
        /* Die Spaltenbreite stand als Inline-Stil hier: 350px liessen sich von
           keiner Medienabfrage ueberschreiben und schoben die Seite auf dem
           Telefon waagerecht aus dem Bild. Jetzt eine Klasse. */
        <div className="crm-split">
          <section className="card">
            <div className="card__body stack" style={{ gap: 'var(--space-3)' }}>
              <input
                type="search"
                className="input"
                placeholder="Rezensionsfälle durchsuchen …"
                value={searchFilter}
                onChange={(e) => setSearchFilter(e.target.value)}
              />
              <select
                className="input"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="">Alle Stufen</option>
                {data.stages.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>

              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 'var(--space-2)',
                  marginTop: '8px',
                }}
              >
                {filteredDeals.length === 0 ? (
                  <p className="muted text-sm">Keine Deals gefunden.</p>
                ) : (
                  filteredDeals.map((deal) => (
                    <button
                      key={deal.id}
                      type="button"
                      onClick={() => setSelectedDealId(deal.id)}
                      className="card"
                      style={{
                        padding: '12px',
                        textAlign: 'left',
                        cursor: 'pointer',
                        borderColor: selectedDealId === deal.id ? 'var(--color-accent)' : undefined,
                        background:
                          selectedDealId === deal.id ? 'var(--color-surface-hover)' : undefined,
                      }}
                    >
                      <div style={{ fontWeight: 600 }}>{deal.title}</div>
                      <div
                        className="muted text-sm"
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          marginTop: '4px',
                        }}
                      >
                        <span>{formatMoney(deal.value, data.pipeline.currency)}</span>
                        <span className="badge">{deal.stageName}</span>
                      </div>
                    </button>
                  ))
                )}
              </div>
            </div>
          </section>

          <section className="card">
            <div className="card__body">
              {selectedDealId ? (
                <div>
                  <Link to="/crm/$id" params={{ id: selectedDealId }}>
                    <Button variant="secondary">Vollständige Detailansicht öffnen ↗</Button>
                  </Link>
                </div>
              ) : (
                <p className="muted text-center" style={{ padding: '48px 0' }}>
                  Wähle einen Deal aus der linken Liste, um Details anzuzeigen.
                </p>
              )}
            </div>
          </section>
        </div>
      ) : (
        <div className="board">
          {data.stages.map((stage) => (
            <section
              key={stage.id}
              className="board__column"
              data-over={over === stage.id ? 'true' : undefined}
              data-kind={stage.type}
              onDragOver={(event) => {
                event.preventDefault();
                setOver(stage.id);
              }}
              onDragLeave={() => setOver((current) => (current === stage.id ? null : current))}
              onDrop={(event) => handleDrop(event, stage.id)}
            >
              <header className="board__head">
                <h2>{stage.name}</h2>
                <span className="muted text-sm">
                  {stage.deals.length} · {formatMoney(stage.sum, data.pipeline.currency)}
                </span>
                {stage.type === 'open' && (
                  <span className="board__odds" title="Wahrscheinlichkeit für den Forecast">
                    {stage.probability} %
                  </span>
                )}
              </header>

              <div className="board__cards">
                {stage.deals.map((deal) => (
                  <article
                    key={deal.id}
                    className="deal"
                    draggable
                    data-rotting={deal.rotting ? 'true' : undefined}
                    onDragStart={(event) => {
                      event.dataTransfer.setData('text/plain', deal.id);
                      event.dataTransfer.effectAllowed = 'move';
                      setDragging(deal.id);
                    }}
                    onDragEnd={() => setDragging(null)}
                  >
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        marginBottom: '4px',
                      }}
                    >
                      <div
                        style={{
                          width: '28px',
                          height: '28px',
                          borderRadius: '50%',
                          background: 'var(--color-accent-subtle)',
                          color: 'var(--color-accent)',
                          display: 'grid',
                          placeItems: 'center',
                          fontWeight: 700,
                          fontSize: 'var(--text-xs)',
                        }}
                      >
                        {deal.title.slice(0, 2).toUpperCase()}
                      </div>
                      <Link to="/crm/$id" params={{ id: deal.id }} className="deal__title">
                        {deal.title}
                      </Link>
                    </div>
                    <div className="deal__meta">
                      <strong>{formatMoney(deal.value, data.pipeline.currency)}</strong>
                      {deal.rotting && (
                        <span className="badge" data-tone="warning" title="Seit zu langem unverändert">
                          ⚠️ liegengeblieben
                        </span>
                      )}
                    </div>

                    {/*
                    Ohne diese Auswahl waere die Tafel nur mit Maus bedienbar.
                    Sie ruft denselben Endpunkt auf wie das Ablegen.
                  */}
                    <label className="deal__move">
                      <span className="sr-only">Stufe von {deal.title} ändern</span>
                      <select
                        className="input input--compact"
                        value={stage.id}
                        onChange={(event) =>
                          move.mutate({ id: deal.id, stageId: event.target.value })
                        }
                      >
                        {data.stages.map((option) => (
                          <option key={option.id} value={option.id}>
                            {option.name}
                          </option>
                        ))}
                      </select>
                    </label>
                  </article>
                ))}

                {stage.deals.length === 0 && <p className="board__empty muted text-sm">Leer</p>}
              </div>
            </section>
          ))}
        </div>
      )}

      {creating && <NewDealDialog stages={data.stages} onClose={() => setCreating(false)} />}
    </div>
  );
}

function NewDealDialog({ stages, onClose }: { stages: BoardStage[]; onClose: () => void }) {
  const create = useCreateDeal();
  const [title, setTitle] = useState('');
  const [value, setValue] = useState('0');
  const [stageId, setStageId] = useState(stages[0]?.id ?? '');
  const [search, setSearch] = useState('');
  const [chosen, setChosen] = useState<Array<{ id: string; email: string }>>([]);

  const debounced = useDebounced(search, 250);
  const contacts = useQuery({
    ...contactsQuery({ search: debounced }),
    enabled: debounced.trim().length >= 2,
  });

  async function submit() {
    await create.mutateAsync({
      title: title.trim(),
      value: Number(value) || 0,
      stageId,
      contactIds: chosen.map((c) => c.id),
    });
    onClose();
  }

  return (
    <div className="overlay" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="card dialog" role="dialog" aria-modal="true" aria-label="Neuer Rezensionsfall">
        <div className="card__body stack">
          <h2 style={{ fontSize: 'var(--text-lg)' }}>💼 Neuer Rezensionsfall</h2>

          {create.isError && (
            <div className="alert" data-tone="danger" role="alert">
              {create.error instanceof Error ? create.error.message : 'Anlegen fehlgeschlagen.'}
            </div>
          )}

          <Field
            label="Bezeichnung / Referenz"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="z. B. Google Rezensionsfall - Löschung Muster AG"
          />
          <Field
            label="Fallwert / Streitwert"
            type="number"
            min="0"
            value={value}
            onChange={(e) => setValue(e.target.value)}
          />

          <div className="field">
            <label className="field__label" htmlFor="deal-stage">
              Stufe
            </label>
            <select
              id="deal-stage"
              className="input"
              value={stageId}
              onChange={(e) => setStageId(e.target.value)}
            >
              {stages.map((stage) => (
                <option key={stage.id} value={stage.id}>
                  {stage.name}
                </option>
              ))}
            </select>
          </div>

          <div className="field">
            <label className="field__label" htmlFor="deal-contacts">
              Beteiligte
            </label>
            {/*
              Mehrere Beteiligte sind der Regelfall. Die starre 1:1-Bindung
              des Altsystems zwang dazu, den zweiten Ansprechpartner in eine
              Notiz zu schreiben.
            */}
            <input
              id="deal-contacts"
              className="input"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Kontakt suchen (ab 2 Zeichen)"
            />
            {chosen.length > 0 && (
              <div className="tag-row" style={{ marginTop: 'var(--space-2)' }}>
                {chosen.map((contact) => (
                  <button
                    key={contact.id}
                    type="button"
                    className="badge"
                    onClick={() => setChosen((c) => c.filter((x) => x.id !== contact.id))}
                    title="Entfernen"
                  >
                    {contact.email} ×
                  </button>
                ))}
              </div>
            )}
            {contacts.data && contacts.data.items.length > 0 && (
              <ul className="picker">
                {contacts.data.items.slice(0, 6).map((contact) => (
                  <li key={contact.id}>
                    <button
                      type="button"
                      onClick={() => {
                        setChosen((current) =>
                          current.some((c) => c.id === contact.id)
                            ? current
                            : [...current, { id: contact.id, email: contact.email }],
                        );
                        setSearch('');
                      }}
                    >
                      {contact.email}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="dialog__actions">
            <Button variant="secondary" onClick={onClose}>
              Abbrechen
            </Button>
            <Button
              onClick={submit}
              disabled={title.trim().length === 0}
              loading={create.isPending}
            >
              Anlegen
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
